import React from 'react';
import { type Control, type UseFormRegister, type FieldErrors, Controller, type UseFormWatch, type UseFormSetValue, type UseFieldArrayRemove, type UseFieldArrayAppend } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import type { PurchaseOrderFormData } from '../../types/purchaseOrder';
import type { InventoryItem } from '../../types/inventory';
import { AutocompleteSelect } from '../common/AutocompleteSelect';
import { formatCurrency, formatRupiah, parseRupiah } from '../../utils/format';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { UnitToggle } from '../common/UnitToggle';
import { WeightInput } from '../common/WeightInput';
import { Tooltip } from '../common/Tooltip';
import { useLanguage } from '../../features/language/useLanguage';

interface Props {
  control: Control<PurchaseOrderFormData>;
  register: UseFormRegister<PurchaseOrderFormData>;
  errors: FieldErrors<PurchaseOrderFormData>;
  watch: UseFormWatch<PurchaseOrderFormData>;
  setValue: UseFormSetValue<PurchaseOrderFormData>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields: any[];
  append: UseFieldArrayAppend<PurchaseOrderFormData, 'items'>;
  remove: UseFieldArrayRemove;
  inventoryItems: InventoryItem[];
  currencySymbol: string;
  setCreatingItemIndex: (index: number) => void;
  setIsCreateItemModalOpen: (open: boolean) => void;
  setCreateItemInitialName: (name: string | undefined) => void;
  mode?: 'edit' | 'receive';
  isPersistedOrder?: boolean;
  readOnly?: boolean;
  /**
   * Hides every path that opens the "create inventory item" modal (the
   * "+ New Item" option and the free-text fallback). Used during the guided
   * tour: that modal renders on top of the onboarding overlay, so the guide
   * loses its anchor and the step can no longer be completed. The tour only
   * ever wants the tagged sample green beans picked from the list.
   */
  disableItemCreation?: boolean;
}

export const POItemsList: React.FC<Props> = ({
  control,
  register,
  errors,
  watch,
  setValue,
  fields,
  append,
  remove,
  inventoryItems,
  currencySymbol,
  setCreatingItemIndex,
  setIsCreateItemModalOpen,
  setCreateItemInitialName,
  mode = 'edit',
  isPersistedOrder = false,
  readOnly = false,
  disableItemCreation = false
}) => {
  const { t } = useLanguage();
  const { unit, setUnit } = useWeightUnit();
  const watchedItems = watch('items');
  const watchedDeliveryStatus = watch('deliveryStatus');

  const totalAmount = watchedItems?.reduce((sum, item) => {
    return sum + ((item.quantity || 0) * (item.unitPrice || 0));
  }, 0) || 0;

  const isReceiveMode = mode === 'receive';
  const isLocked = readOnly || (isPersistedOrder && !isReceiveMode && watchedDeliveryStatus === 'received');
  const showArrival = watchedDeliveryStatus === 'received';
  const openCreateItemModal = (index: number, initialName?: string) => {
    setCreatingItemIndex(index);
    setCreateItemInitialName(initialName);
    const activeEl = document.activeElement as HTMLElement | null;
    activeEl?.blur?.();
    setTimeout(() => setIsCreateItemModalOpen(true), 0);
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <div className="flex justify-between items-center mb-4 border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">{t('salesOrder.items.items')}</h3>
        <div className="flex items-center gap-2">
          <UnitToggle unit={unit} onToggle={setUnit} disabled={isReceiveMode || isLocked} />
          {!isReceiveMode && !isLocked && (
            <div className="flex gap-2">
              <button
                data-test-id="po-create-add-item"
                type="button"
                onClick={() => append({ inventoryItemId: '', quantity: 0, unitPrice: 0, name: '', weightReceived: 0, itemCode: '', sampleItem: false })}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200 transition-colors"
              >
                <Plus className="h-4 w-4 mr-1" /> {t('purchaseOrder.items.addItem')}
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="space-y-4">
        {fields.map((field, index) => (
          <div key={field.id} className="flex gap-x-6 gap-y-4 items-start pb-4 border-b border-gray-200 last:border-0 last:pb-0 flex-wrap 2xl:flex-nowrap">
            <div className="flex-[2] min-w-[200px] relative">
              <label className="block text-sm font-medium text-gray-700">
                {t('reports.shift.item')}
              </label>
              <div className="mt-1">
                {/* Register hidden field for inventoryItemId to ensure it's tracked for validation */}
                <input type="hidden" {...register(`items.${index}.inventoryItemId`)} />
              
                {isReceiveMode || isLocked ? (
                  <Tooltip content={watchedItems[index]?.name || t('purchaseOrder.form.values.unknownItem')}>
                    <div 
                      className="py-2.5 text-sm text-gray-900 font-medium bg-gray-50 px-3 rounded border border-gray-200 truncate"
                    >
                       {watchedItems[index]?.name || t('purchaseOrder.form.values.unknownItem')}
                    </div>
                  </Tooltip>
                ) : (
                <>
                <Controller
                  control={control}
                  name={`items.${index}.name`}
                  render={({ field: { onChange, value } }) => (
                    <AutocompleteSelect
                      dataTestId={`po-create-item-name-${index}`}
                      inputClassName="truncate"
                      value={value}
                      onChange={(text, option) => {
                        if (option?.id === 'NEW_ITEM') {
                          onChange('');
                          setValue(`items.${index}.inventoryItemId`, '', { shouldValidate: true });
                          setValue(`items.${index}.unitPrice`, 0);
                          setValue(`items.${index}.itemCode`, '');
                          setValue(`items.${index}.lotId`, undefined);
                          setValue(`items.${index}.lotNumber`, undefined);
                          openCreateItemModal(index, undefined);
                          return;
                        }

                        // Typing a name that matches nothing normally offers to
                        // create that item; during the tour it must not, for the
                        // same reason "+ New Item" is hidden below.
                        if (!disableItemCreation && !option && text.trim().length > 0) {
                          openCreateItemModal(index, text.trim());
                        }

                        onChange(option ? option.text : text);
                        // Clear lot info when item changes as it would no longer be valid
                        setValue(`items.${index}.lotId`, undefined);
                        setValue(`items.${index}.lotNumber`, undefined);
                        
                        if (option) {
                          setValue(`items.${index}.inventoryItemId`, option.id, { shouldValidate: true });
                          const item = inventoryItems.find(i => i.id === option.id);
                          if (item) {
                            setValue(`items.${index}.unitPrice`, item.standardCost || 0);
                            setValue(`items.${index}.itemCode`, item.code); // Use inventory code if selected
                          }
                        } else {
                          // Allow creation of new item: Clear ID but keep text (handled by onChange(text) above)
                          setValue(`items.${index}.inventoryItemId`, '', { shouldValidate: true }); 
                          setValue(`items.${index}.unitPrice`, 0);
                          
                          // Set item code to empty for new items
                          setValue(`items.${index}.itemCode`, '');
                        }
                      }}
                      options={[
                        ...(disableItemCreation
                          ? []
                          : [{ id: 'NEW_ITEM', label: '+ New Item', text: '+ New Item' }]),
                        ...inventoryItems
                          .filter(i => i.itemType === 'green_bean')
                          .map(i => ({
                            id: i.id,
                            label: `${i.name} (${i.code})`,
                            text: i.name
                          }))
                      ]}
                      error={errors.items?.[index]?.inventoryItemId?.message}
                      placeholder={t('purchaseOrder.items.selectItem')}
                    />
                  )}
                />
                </>
                )}
                {watchedItems[index]?.lotNumber && (
                  <p
                    className="text-[10px] text-gray-500 mt-1 truncate"
                    title={`${t('inventory.history.lotNumber')}: ${watchedItems[index].lotNumber}`}
                  >
                    {t('inventory.history.lotNumber')}: {watchedItems[index].lotNumber}
                  </p>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-[100px]">
              <label className="block text-sm font-medium text-gray-700">{t('reports.export.columns.itemCode')}</label>
              <input
                {...register(`items.${index}.itemCode`)}
                disabled
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm border p-2.5 bg-gray-100 text-gray-500 cursor-not-allowed"
              />
            </div>

            <div className="flex-1 min-w-[80px]">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-700">{t('purchaseOrder.items.weight')}</label>
              </div>
              <Controller
                control={control}
                name={`items.${index}.quantity`}
                render={({ field: { onChange, value } }) => (
                  <WeightInput
                    data-test-id={`po-create-item-quantity-${index}`}
                    readOnly={isReceiveMode || isLocked}
                    value={value}
                    onChange={(val) => {
                      onChange(val);
                      setValue(`items.${index}.weightReceived`, val);
                    }}
                    unit={unit}
                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2.5 ${isReceiveMode || isLocked ? 'bg-gray-100' : ''}`}
                  placeholder={t('inventory.history.placeholderKg')}
                  />
                )}
              />
              {errors.items?.[index]?.quantity && (
                <p className="text-xs text-red-600 mt-1">{errors.items[index]?.quantity?.message}</p>
              )}
            </div>

            {showArrival && (
              <>
                <div className="flex-1 min-w-[80px]">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-gray-700">{t('purchaseOrder.items.arrival')}</label>
                  </div>
                  <Controller
                    control={control}
                    name={`items.${index}.weightReceived`}
                    render={({ field: { onChange, value } }) => (
                      <WeightInput
                        data-test-id={`po-create-item-arrival-${index}`}
                        readOnly={isLocked}
                        value={value}
                        onChange={(val) => onChange(val)}
                        unit={unit}
                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2.5 ${isLocked ? 'bg-gray-100' : 'bg-white'}`}
                        placeholder={t('inventory.history.placeholderKg')}
                      />
                    )}
                  />
                  {errors.items?.[index]?.weightReceived && (
                    <p className="text-[10px] text-red-600 mt-1 leading-tight font-medium">
                      {errors.items[index]?.weightReceived?.message || t('purchaseOrder.items.arrivalExceedsBuyWeight')}
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="flex-1 min-w-[140px]">
              <label className="block text-sm font-medium text-gray-700">{t('reports.details.unitPrice')}</label>
              <Controller
                control={control}
                name={`items.${index}.unitPrice`}
                render={({ field: { value, onChange } }) => (
                  <input
                    type="text"
                    value={formatRupiah(value)}
                    onChange={(e) => onChange(parseRupiah(e.target.value))}
                    disabled={isReceiveMode || readOnly}
                    data-test-id={`po-create-item-unit-price-${index}`}
                    className={`mt-1 block w-full rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2.5 transition-colors ${
                      isReceiveMode || readOnly ? 'bg-gray-100 text-gray-500' :
                      value === 0 ? 'border-red-400 bg-red-50/30' : 'border-gray-300'
                    }`}
                  />
                )}
              />
              {errors.items?.[index]?.unitPrice ? (
                <p className="text-[10px] text-red-600 leading-tight font-medium mt-1">{errors.items[index]?.unitPrice?.message}</p>
              ) : watchedItems?.[index]?.unitPrice === 0 && (
                <p className="text-[10px] text-red-600 leading-tight font-semibold italic mt-1">{t('purchaseOrder.items.unitPriceGreaterThanZero')}</p>
              )}
            </div>

            <div className="flex-none min-w-[110px] pt-[26px]">
              <div className="flex items-center gap-2 h-[42px]">
                <input
                  data-test-id={`po-create-item-sample-${index}`}
                  type="checkbox"
                  disabled={isReceiveMode || isLocked}
                  {...register(`items.${index}.sampleItem`)}
                  className="h-4 w-4"
                />
                <label className="text-sm text-gray-700">{t('purchaseOrder.items.sampleItem')}</label>
              </div>
            </div>

            <div className="flex-1 min-w-[120px] flex items-center justify-end gap-3 text-sm font-medium whitespace-nowrap pt-[26px] h-full min-h-[68px]">
              <span className="py-2.5">{formatCurrency((watchedItems?.[index]?.quantity || 0) * (watchedItems?.[index]?.unitPrice || 0), currencySymbol)}</span>
              {!isReceiveMode && !isLocked && (
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="text-red-600 hover:text-red-800 transition-colors flex-none"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex justify-end text-lg font-semibold text-gray-900">
        {t('reports.details.total')}: {formatCurrency(totalAmount, currencySymbol)}
      </div>
    </div>
  );
};
